import { GAME_HEIGHT, GAME_WIDTH } from "../../data/constants"
import type { EnemyData, GameSprite } from "../../types"
import {
  type EnemyState,
  type EnemyStateContext,
  StatefulEnemyRuntime,
} from "../enemy-system"

const STAR_LOCK_X = GAME_WIDTH * 0.56
const STAR_APPROACH_SPEED = 3.7
const STAR_EXIT_SPEED = 2.6
const STAR_BOB_AMPLITUDE = 14
const STAR_BOB_PERIOD_MS = 1200
const STAR_LOCK_DURATION_MS = 6500
const STAR_WARNING_MS = 360

type RouterPhase = "approach" | "warning" | "lockdown" | "exit"

export class RouterEnemy extends StatefulEnemyRuntime<RouterPhase> {
  public constructor(sprite: GameSprite, enemyData: EnemyData) {
    const states: EnemyState<RouterPhase>[] = [
      {
        name: "approach",
        behaviorState: "tracking",
        animationState: "tracking",
        canFire: false,
        update: (context) => this.updateApproach(context),
      },
      {
        name: "warning",
        behaviorState: "focusing",
        animationState: "focusing",
        canFire: false,
        enter: (context) => {
          context.sprite.setTint(0xfacc15)
          context.vfx.emitImpactBurst(
            context.sprite.x,
            context.sprite.y,
            0xfacc15,
            0.85,
          )
        },
        update: (context) => this.updateWarning(context),
      },
      {
        name: "lockdown",
        behaviorState: "attacking",
        animationState: "attacking",
        canFire: true,
        enter: (context) => {
          context.sprite.clearTint()
          context.enemyData.lastShot = context.enemyData.shootCooldown
        },
        update: (context) => this.updateLockdown(context),
      },
      {
        name: "exit",
        behaviorState: "retreating",
        animationState: "retreating",
        canFire: false,
        enter: (context) => {
          context.sprite.setTint(0xa78bfa)
        },
        update: (context) => this.updateExit(context),
      },
    ]

    super(sprite, enemyData, "approach", states)
  }

  public override onHeroCollision():
    | "damage-only"
    | "damage-and-defeat"
    | null {
    return "damage-and-defeat"
  }

  private updateApproach(
    context: EnemyStateContext<RouterPhase>,
  ): RouterPhase | null {
    const targetY = GAME_HEIGHT * 0.5
    context.enemyData.vx = context.scrollCompensation - STAR_APPROACH_SPEED
    context.enemyData.vy = (targetY - context.sprite.y) * 0.045

    if (context.sprite.x <= STAR_LOCK_X) {
      context.sprite.x = STAR_LOCK_X
      context.enemyData.x = STAR_LOCK_X
      return "warning"
    }

    return null
  }

  private updateWarning(
    context: EnemyStateContext<RouterPhase>,
  ): RouterPhase | null {
    this.holdStarPosition(context)
    context.sprite.angle = Math.sin(context.stateTime / 45) * 8

    if (context.stateTime >= STAR_WARNING_MS) {
      return "lockdown"
    }

    return null
  }

  private updateLockdown(
    context: EnemyStateContext<RouterPhase>,
  ): RouterPhase | null {
    this.holdStarPosition(context)

    const patternIndex = context.enemyData.routerPatternIndex ?? 0
    const targetAngle = patternIndex % 2 === 0 ? 0 : 45
    const wobble = Math.sin(context.time / 160) * 4
    context.sprite.setAngle(targetAngle + wobble)

    if (context.stateTime >= STAR_LOCK_DURATION_MS) {
      return "exit"
    }

    return null
  }

  private updateExit(
    context: EnemyStateContext<RouterPhase>,
  ): RouterPhase | null {
    context.enemyData.vx = context.scrollCompensation - STAR_EXIT_SPEED
    context.enemyData.vy *= 0.9

    if (context.sprite.x < -80) {
      context.sprite.destroy()
    }

    return null
  }

  private holdStarPosition(context: EnemyStateContext<RouterPhase>): void {
    const spec = context.enemyDef?.movementSpec
    const bobAmplitude =
      spec?.action === "router-star-bob"
        ? spec.bobAmplitude
        : STAR_BOB_AMPLITUDE
    const bobPeriodMs =
      spec?.action === "router-star-bob" ? spec.bobPeriodMs : STAR_BOB_PERIOD_MS

    context.sprite.x = STAR_LOCK_X
    context.enemyData.x = STAR_LOCK_X
    context.enemyData.vx = context.scrollCompensation
    context.enemyData.vy = 0
    context.sprite.y =
      GAME_HEIGHT * 0.5 + Math.sin(context.time / bobPeriodMs) * bobAmplitude
    context.enemyData.y = context.sprite.y
  }
}
